import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Row, Col, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { BookStatusBadge } from '../components/BookProgressBar';
import { listMyOrders } from '../actions/orderActions';
import { getMyGenaiJobs } from '../actions/genaiActions';

function OrderHistoryScreen() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const userLogin = useSelector(state => state.userLogin);
    const { userInfo } = userLogin;

    const orderListMy = useSelector(state => state.orderListMy);
    const { loading, error, orders } = orderListMy;

    const genaiMyJobs = useSelector(state => state.genaiMyJobs);
    const { jobs: allJobs } = genaiMyJobs || {};

    // Build map: orderId → most-recent job
    const jobByOrderId = React.useMemo(() => {
        const map = {};
        (allJobs || []).forEach(j => {
            const oid = j.order_id;
            if (!oid) return;
            if (!map[oid] || new Date(j.updated_at) > new Date(map[oid].updated_at)) {
                map[oid] = j;
            }
        });
        return map;
    }, [allJobs]);

    useEffect(() => {
        if (!userInfo) {
            navigate('/login');
        } else {
            dispatch(listMyOrders());
            dispatch(getMyGenaiJobs());
        }
    }, [dispatch, navigate, userInfo]);

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('en-US', options);
    };

    return (
        <div>
            <Row className="align-items-center mb-4">
                <Col>
                    <h1>{t('orderHistory.title')}</h1>
                </Col>
                <Col className="text-end">
                    <LinkContainer to='/products'>
                        <Button variant="primary">
                            <i className="fas fa-shopping-cart"></i> {t('orderHistory.continueShopping')}
                        </Button>
                    </LinkContainer>
                </Col>
            </Row>

            {loading ? (
                <Loader />
            ) : error ? (
                <Message variant='danger'>{error}</Message>
            ) : orders && orders.length === 0 ? (
                <Message variant='info'>
                    {t('orderHistory.noOrders')} <LinkContainer to='/products'><a href="/products">{t('orderHistory.startShopping')}</a></LinkContainer>
                </Message>
            ) : (
                <>
                    <p className="text-muted">
                        {t('orderHistory.count', { count: orders.length })}
                    </p>
                    <Table striped bordered hover responsive className='table-sm'>
                        <thead>
                            <tr>
                                <th>{t('orderHistory.colOrderId')}</th>
                                <th>{t('orderHistory.colDate')}</th>
                                <th>{t('orderHistory.colTotal')}</th>
                                <th>{t('orderHistory.colPayment')}</th>
                                <th>{t('orderHistory.colBook')}</th>
                                <th>{t('orderHistory.colDelivery')}</th>
                                <th>{t('orderHistory.colDetails')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((order) => (
                                <tr key={order.OrderId}>
                                    <td>#{order.OrderId}</td>
                                    <td>{formatDate(order.created_at)}</td>
                                    <td>{order.total_price} {t('common.dkk')}</td>
                                    <td>
                                        {order.is_paid ? (
                                            <Badge bg="success">
                                                {t('orderHistory.paid', { date: formatDate(order.paid_at) })}
                                            </Badge>
                                        ) : (
                                            <Badge bg="warning" text="dark">
                                                {t('orderHistory.notPaid')}
                                            </Badge>
                                        )}
                                    </td>
                                    <td>
                                        <BookStatusBadge job={jobByOrderId[order.OrderId] || null} />
                                    </td>
                                    <td>
                                        {(order.is_delivered || order.is_all_digital) ? (
                                            <Badge bg="success">
                                                {order.is_all_digital ? t('orderHistory.delivered') : t('orderHistory.deliveredDate', { date: formatDate(order.delivered_at) })}
                                            </Badge>
                                        ) : order.is_paid ? (
                                            <Badge bg="info">{t('orderHistory.awaitingShipping')}</Badge>
                                        ) : (
                                            <Badge bg="secondary">{t('orderHistory.notPaid')}</Badge>
                                        )}
                                    </td>
                                    <td>
                                        <LinkContainer to={`/order/${order.OrderId}`}>
                                            <Button variant='primary' size='sm'>
                                                {t('orderHistory.viewDetails')}
                                            </Button>
                                        </LinkContainer>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>
            )}
        </div>
    );
}

export default OrderHistoryScreen;
